import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKeyhole, KeyRound } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { COMPANY as C } from "../constants/company";

const DemoLoginForm = () => {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(username, password);
      navigate("/app");
    } catch (ex) {
      setErr(ex.response?.status === 429 ? t.login.locked : t.login.invalid);
    } finally { setBusy(false); }
  };

  const input = "mt-2 w-full rounded-xl border border-line bg-canvas px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100";
  return (
    <motion.form
      data-testid="demo-login-form"
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="card-soft relative w-full max-w-md p-8"
    >
      <span className="absolute right-6 top-6 rounded-full bg-ink px-2.5 py-1 font-mono text-[0.6rem] font-semibold tracking-widest text-white">{t.demo.ribbon}</span>
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700"><KeyRound size={18} /></span>
      <p className="eyebrow mt-6 text-violet-700">{t.login.demoEyebrow}</p>
      <h2 className="font-display mt-2 text-xl font-extrabold leading-tight text-ink">{t.login.demoTitle}</h2>
      <p className="mt-3 text-sm leading-relaxed text-ink-2">{t.login.demoBody}</p>
      <label htmlFor="demo-user" className="mt-6 block text-sm font-semibold text-ink">{t.login.username}</label>
      <input id="demo-user" data-testid="demo-username-input" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} className={input} />
      <label htmlFor="demo-pass" className="mt-4 block text-sm font-semibold text-ink">{t.login.password}</label>
      <input id="demo-pass" data-testid="demo-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
      {err && <p data-testid="demo-login-error" className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      <button data-testid="demo-login-button" disabled={busy || !username || !password} className="pill btn-primary mt-6 w-full py-3 text-sm font-semibold disabled:opacity-60">{busy ? t.login.submitting : t.login.submit}</button>
    </motion.form>
  );
};

export default function Login() {
  const { t } = useLang();
  return (
    <main data-testid="login-page" className="mx-auto max-w-7xl px-4 pb-28 pt-28 sm:px-6 lg:px-8">
      <div className="grid items-start gap-8 lg:grid-cols-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="hero-card grain relative overflow-hidden rounded-[2rem] border border-white/70 p-8 sm:p-12 lg:col-span-7">
          <div className="relative">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-white"><LockKeyhole size={20} /></span>
            <p className="eyebrow mt-8 text-violet-700">{t.login.eyebrow}</p>
            <h1 data-testid="login-title" className="font-display mt-3 text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">{t.login.title}</h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-2 sm:text-lg">{t.login.body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#waitlist" data-testid="login-waitlist-cta" className="pill btn-primary px-6 py-3 text-sm font-semibold">{t.login.cta}</Link>
              <Link to="/" data-testid="login-back-link" className="pill btn-ghost px-6 py-3 text-sm font-semibold">{t.login.back}</Link>
            </div>
            <p className="eyebrow mt-10 text-[0.58rem] text-mist">{t.hero.comingSoon} · {C.legalName} · {C.country}</p>
          </div>
        </motion.div>
        <div className="flex justify-center lg:col-span-5 lg:justify-end">
          <DemoLoginForm />
        </div>
      </div>
    </main>
  );
}
